import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { useAuthStore } from '@/stores/auth.store'
import { fetchApi } from '@/lib/api-client'
import { applicantService } from '@/services/applicant.service'

export function ScriptViewerPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { token } = useAuthStore()
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [resolvedVersion, setResolvedVersion] = useState<string>('')

  const versionParam = searchParams.get('version')

  useEffect(() => {
    async function fetchScript() {
      try {
        setLoading(true)

        let version = versionParam
        if (!version) {
          version = await applicantService.getLatestScriptVersion(token!)
        }
        if (!version) {
          setError('No script versions available')
          return
        }

        setResolvedVersion(version)
        const data = await fetchApi<{ content: string; version: string }>(
          `/scripts/${version}`
        )
        setContent(data.content)
      } catch (err) {
        console.error('Script fetch error:', err)
        setError(err instanceof Error ? err.message : 'Failed to load script')
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      fetchScript()
    }
  }, [versionParam, token])

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">{t('common.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - hidden when printing */}
      <header className="bg-white shadow print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('applicant.scriptTitle', { version: resolvedVersion })}</h1>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('applicant.print')}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('common.back')}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 print:shadow-none">
          <article className="prose prose-sm max-w-none prose-p:my-1 prose-headings:mt-4 prose-headings:mb-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 leading-snug">
            <ReactMarkdown>{content}</ReactMarkdown>
          </article>
        </div>
      </main>
    </div>
  )
}
